import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import ExperienceSearchBar from './ExperienceSearchBar/ExperienceSearchBar'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import TextButton from 'LuxKit/Button/TextButton'
import ExperiencesSearchForm from './ExperienceSearchForm'
import Pane from 'components/Common/Pane'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import ExperienceSearchMapFilters from './ExperienceSearchMapFilter'
import MapSearchNav from 'components/NavKit/MapSearchNav'
import noop from 'lib/function/noop'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'

const Wrapper = styled(Pane)`
  display: grid;
  padding-bottom: ${rem(16)};
  padding-top: ${rem(16)};
  gap: ${rem(16)};

  ${mediaQueryDown.tablet} {
    padding-bottom: ${rem(8)};
    padding-top: ${rem(8)};
  }
`

const StyledExperienceSearchBar = styled(ExperienceSearchBar)`
  ${mediaQueryUp.tablet} {
    display: none;
  }

  &.edit-mode {
    display: none;
  }
`

const SearchFormContainer = styled(LayoutContainer)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;

    &.map-view {
      flex: 1;
      padding: 0;
      display: flex;
      gap: ${rem(8)};
      width: 100%;
      padding-right: ${rem(24)};
    }
  }

  &.map-view {
    ${mediaQueryDown.tablet} {
      display: none;
    }
  }
`

const FilterContainer = styled(LayoutContainer)`
  display: flex;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
  }
`

const StyledButton = styled(TextButton)`
  flex: 1;

  ${mediaQueryUp.tablet} {
    flex: unset
  }
`

const SortAndFilterButton = styled(StyledButton)`
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const CategoriesFilterContainer = styled(LayoutContainer)`
  max-width: 100% !important;
  padding: ${rem(20)};
  padding-bottom: 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  ${mediaQueryDown.tablet} {
    display: none;
  }
`

interface Props {
  list: App.ApiCallState<{
    ids: Array<string>
    placeId: string
  }>
  toggleShowFilter?: () => void
  searchMode: Experiences.SearchMode
  location?: string
  experiences?: Array<App.ExperienceOffer>
  isFetching?: boolean
  place?: App.Place
}

function ExperienceSearchHeader(props: Props) {
  const { toggleShowFilter = noop, list, searchMode, location, experiences, isFetching = false, place } = props
  const trackEvent = useTrackExperienceOfferEvent()
  const intl = useIntl()

  const handleViewMapClick = useCallback(() => {
    trackEvent('VIEW_ON_MAP_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const handleViewAllClick = useCallback(() => {
    trackEvent('VIEW_ALL_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const isMapView = searchMode.mode === 'map'

  const sortAndFilterLabel = searchMode.mode === 'list'
    ? intl.formatMessage({
        id: 'UcYoljwgiDaQBr-_tXJD',
        defaultMessage: 'Sort & Filter',
        description: 'Button text to open sort and filter panel in experience search list view',
      })
    : intl.formatMessage({
        id: 'di3Vcyq0jIcSBVN96vKz',
        defaultMessage: 'Filter',
        description: 'Button text to open filter panel in experience search',
      })

  return <Wrapper>
    <StyledExperienceSearchBar
      isMapView={isMapView}
      location={location}
      searchModeUrl={searchMode.url}
    />
    <SearchFormContainer className={clsx({ 'map-view': isMapView })}>
      {isMapView && <CSSBreakpoint min="tablet">
        <StyledButton
          kind="tertiary"
          size="large"
          onClick={handleViewAllClick}
          startIcon={<LineArrowLeftIcon />}
          fit="mobile-full-width"
          to={searchMode.url}
        >
          {intl.formatMessage({
            id: '_4K2AVKzyOGSbv_5CdYa',
            defaultMessage: 'List view',
            description: 'Button text to switch from map view to list view on experience search',
          })}
        </StyledButton>
      </CSSBreakpoint>}
      <ExperiencesSearchForm isMapView={isMapView} />
      {isMapView && <MapSearchNav />}
    </SearchFormContainer>
    {!isMapView && (
      <FilterContainer>
        {searchMode.mode === 'list' &&
          <CSSBreakpoint min="desktop" as="span">
            <ExperienceSearchPageSort />
          </CSSBreakpoint>}
        {list?.ids.length > 0 &&
          <SortAndFilterButton
            kind="secondary"
            variant="dark"
            startIcon={<LineFilterAltIcon />}
            onClick={toggleShowFilter}
            data-testid="sort-and-filter-button"
          >
            {sortAndFilterLabel}
          </SortAndFilterButton>}
        <StyledButton
          kind="secondary"
          variant="dark"
          to={searchMode.url}
          startIcon={<LineMapIcon />}
          onClick={handleViewMapClick}
          fit="mobile-full-width"
        >
          {intl.formatMessage({
            id: 'RbdWKzotOqHA2u5MKIuZ',
            defaultMessage: 'Map view',
            description: 'Button text to switch to map view on experience search',
          })}
        </StyledButton>
      </FilterContainer>
    )}
    {isMapView &&
      <CategoriesFilterContainer>
        <ExperienceSearchMapFilters experiences={experiences} isFetching={isFetching} place={place} />
      </CategoriesFilterContainer>}
  </Wrapper>
}

export default ExperienceSearchHeader
